/**
 * PortfoliosListPage — «Мои портфели» (полировка T-Bank уровня).
 * Информативная компактная карточка: тикер + стратегия, мета (ноги/премия/дата),
 * статус-чип вердикта. Без сырых UUID. Презентационный слой — данные/типы не менялись.
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as api from "../api";

const money = (v: number) => "$" + Math.round(v).toLocaleString("ru-RU");
const fmtDate = (iso: string) => new Date(iso).toLocaleDateString("ru-RU", { day: "numeric", month: "short", year: "2-digit" });

// Выжимка карточки из инструментов (всё — презентация, бизнес-логика не трогается).
function summarize(p: api.Portfolio) {
  const opts = p.instruments.filter((i: any) => i.type === "option");
  const calls = opts.filter((o: any) => o.side === "call").length;
  const puts = opts.filter((o: any) => o.side === "put").length;
  const ticker = (p.instruments.find((i: any) => i.underlying)?.underlying as string) || "—";
  let strategy = "позиция";
  if (calls && puts) {
    const strikesEqual = opts.length === 2 && opts[0].strike === opts[1].strike;
    strategy = strikesEqual ? "стрэдл" : "стрэнгл";
  } else if (opts.length) strategy = calls ? "колл" : "пут";
  const premium = opts.reduce((s: number, o: any) => s + (o.premium_paid ?? 0) * (o.contract_multiplier ?? 1) * (o.quantity ?? 0), 0);
  return { ticker, strategy, legs: p.instruments.length, premium };
}

// Чип вердикта по статусу портфеля (без вызова evaluate на каждую карточку).
function verdict(status: string): { cls: string; label: string } {
  const s = (status || "").toLowerCase();
  if (s.includes("accept") || s === "active" || s === "approved") return { cls: "acc", label: "ПРИНЯТО" };
  if (s.includes("reject")) return { cls: "rej", label: "ОТКЛОНЕНО" };
  return { cls: "draft", label: "черновик" };
}

export function PortfoliosListPage() {
  const [items, setItems] = useState<api.Portfolio[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { api.listPortfolios().then(setItems).catch((e) => setError(e.message)); }, []);

  return (
    <div>
      <div className="page-tools">
        <div className="page-title">Мои портфели</div>
        <Link to="/portfolios/new" className="btn btn-primary">+ Новый портфель</Link>
      </div>

      {error && <div className="card" style={{ padding: 12, color: "var(--bad)" }}>{error}</div>}

      {items === null && !error && (
        <div className="pf-list">
          {[0, 1, 2].map((i) => <div key={i} className="skel" style={{ height: 64 }} />)}
        </div>
      )}

      {items !== null && items.length === 0 && (
        <div className="card"><div className="empty">
          <span className="ic">📁</span>
          <div style={{ fontWeight: 600, color: "var(--txt-2)" }}>Портфелей пока нет</div>
          <div style={{ maxWidth: 340, fontSize: 12.5 }}>Создай первый — собери long-strangle в Аналитике и проверь, проходит ли он 5 правил.</div>
          <Link to="/portfolios/new" className="btn btn-primary" style={{ marginTop: 4 }}>+ Новый портфель</Link>
        </div></div>
      )}

      {items && items.length > 0 && (
        <div className="pf-list">
          {items.map((p) => {
            const s = summarize(p);
            const v = verdict(p.status);
            const generic = !p.name || p.name.trim().toLowerCase() === "новый портфель";
            return (
              <Link key={p.id} to={`/portfolios/${p.id}`} className="pf-card">
                <div>
                  <div className="nm">{generic ? `${s.ticker} · ${s.strategy}` : p.name}{!generic && <span className="tag">{s.ticker} · {s.strategy}</span>}</div>
                  <div className="meta">
                    <span>{s.legs} ног</span>
                    {s.premium > 0 && <><span className="dot">·</span><span>премия <span className="num">{money(s.premium)}</span></span></>}
                    <span className="dot">·</span><span>{fmtDate(p.updated_at)}</span>
                  </div>
                </div>
                <span className={`vchip ${v.cls}`}>{v.label}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
